import { memo, useCallback } from 'react';

import { CheckIcon } from '@air/design-icons';
import { DropdownMenuItem } from '@air/primitive-dropdown-menu';

import { type PublicShowcaseNavEntry } from '~/components/v2/components/showcase/publicShowcaseNavEntries';
import { useExternalLinksClickableContext } from '~/providers/ExternalLinksClickableProvider';
import { usePublicShowcaseContext } from '~/providers/PublicShowcaseProvider';

export type PublicShowcaseNavMenuItemProps = {
  entry: PublicShowcaseNavEntry;
};

/**
 * One destination in the mobile navigation menu. Internal entries drive the layout contract's
 * `onSelectPage` — a shallow route push on the URL surfaces (the root page and every sub-page are
 * the same Next page rendered from the same static props, so there is nothing to refetch), the
 * editor's active-page state in the Edit tab.
 */
export const PublicShowcaseNavMenuItem = memo(({ entry }: PublicShowcaseNavMenuItemProps) => {
  const { onSelectPage } = usePublicShowcaseContext();
  const { areExternalLinksClickable } = useExternalLinksClickableContext();

  const handleSelect = useCallback(() => {
    if (entry.isExternal) {
      if (areExternalLinksClickable) {
        window.open(entry.externalHref, '_blank', 'noopener,noreferrer');
      }

      return;
    }

    onSelectPage(entry.pageId === null || entry.slug === null ? null : { pageId: entry.pageId, pageSlug: entry.slug });
  }, [areExternalLinksClickable, entry.externalHref, entry.isExternal, entry.pageId, entry.slug, onSelectPage]);

  return (
    <DropdownMenuItem
      data-testid="PUBLIC_COLLECTION_NAV_MENU_ITEM"
      label={entry.label}
      onSelect={handleSelect}
      size="small"
      suffix={
        entry.isActive ? (
          <CheckIcon
            className="shrink-0 ads:text-icon-default text-gray-11"
            data-testid="PUBLIC_COLLECTION_NAV_MENU_ITEM_ACTIVE_CHECK"
            size="sm"
          />
        ) : undefined
      }
    />
  );
});

PublicShowcaseNavMenuItem.displayName = 'PublicShowcaseNavMenuItem';
